import {
  Skeleton,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import React from "react";
import styled from "@emotion/styled/macro";
import { useCapContext } from "../../../providers/CapContext";
import { Column, Row } from "../../atoms/layoutAtoms";
import { FinancialTermsRows } from "../../molecules/FinancialTermRows";
import { SystemRows } from "../../molecules/SystemRows";
import { ProductRows } from "../../molecules/ProductRows";
import { ViewType } from "../../../capTypes";
import { FryProps } from "../../molecules/HamburgerMenu";
import { CapCollapsibleTechnicalSpecifications } from "../../molecules/CapCollapsibleTechnicalSpecifications";
import { CapDesignStepComparisonInfographics } from "../CapDesignStepComparisonInfographics";
import {
  type CapBatteryImageKey,
  ComparisonViewImage,
  SingleViewImage,
} from "./DesignStepViewCommon";
import { getComparisonSelfSupplyPercentFromValue } from "utils/selfSupplyChartData";

type DesignRowHeaderProps = {
  title: string;
  links: FryProps[] | null;
  menuLabel: string;
  testId?: string;
};

type DesignImage = { url: string } | undefined;

type SimplifiedDesignStepContentProps = {
  viewType: ViewType;
  isLoading: boolean;
  smallOriginalImage: DesignImage;
  smallProposalImage: DesignImage;
  batteryImageKey: CapBatteryImageKey | undefined;
  showFullSizeImage: boolean;
  openLargeImageCarousel: (imageType: "original" | "new") => void;
  specificationLinks: FryProps[];
  termsLinks: FryProps[] | null;
  toggleProductionModal: () => void;
  DesignRowHeader: React.ComponentType<DesignRowHeaderProps>;
};

const SimplifiedDesignStepContent = ({
  viewType,
  isLoading,
  smallOriginalImage,
  smallProposalImage,
  batteryImageKey,
  showFullSizeImage,
  openLargeImageCarousel,
  specificationLinks,
  termsLinks,
  toggleProductionModal,
  DesignRowHeader,
}: SimplifiedDesignStepContentProps) => {
  const {
    activeProposal,
    sow,
    isCustomer,
    isNonSolar,
    isOnlyBattery,
    isOnlyEvCharger,
  } = useCapContext();
  const isMobile = useSingletonMobileMediaQuery();
  const isComparison = viewType === "Comparison";

  return (
    <DesignCard
      data-testid={
        isComparison
          ? "design-step-view-card-comparison"
          : "design-step-view-card-single"
      }
      isMobile={isMobile}
    >
      <CapDesignStepComparisonInfographics
        mode={isComparison ? "comparison" : "single"}
        activeSelfSupplyPercent={getComparisonSelfSupplyPercentFromValue(
          sow?.annualPercentSelfSupply
        )}
        originalSelfSupplyPercent={getComparisonSelfSupplyPercentFromValue(
          sow?.originalProposal?.annualPercentSelfSupply
        )}
        activeProposalId={activeProposal?.proposalId}
        originalProposalId={sow?.originalProposal?.id}
        utilityCompany={activeProposal?.utility ?? ""}
        isLoading={isLoading}
      />
      <DesignTableContainer>
        <DesignRowHeader
          title="Terms"
          links={termsLinks}
          menuLabel="Edit"
          testId="design-terms-header"
        />
        <FinancialTermsRows
          viewType={viewType}
          isLoading={isLoading}
          showSolarCostPerKwh={false}
        />
        <SolarDesignSection data-testid="cap-solar-design-section">
          <DesignRowHeader
            title="Solar design"
            links={null}
            menuLabel=""
            testId="cap-solar-design-header"
          />
          {isComparison ? (
            <DesignImageRow isMobile={isMobile}>
              <DesignImageColumn>
                {isLoading ? (
                  <DesignImageSkeleton />
                ) : (
                  <ComparisonViewImage
                    title="Original"
                    isOnlyBattery={isOnlyBattery}
                    isOnlyEvCharger={isOnlyEvCharger}
                    batteryImageKey={batteryImageKey}
                    img={smallOriginalImage}
                    onOpenLargeImageCarousel={() =>
                      openLargeImageCarousel("original")
                    }
                    showFullSizeImage={showFullSizeImage}
                    testId="offer-experience--DesignStepView--image-original"
                  />
                )}
              </DesignImageColumn>
              <DesignImageColumn>
                {isLoading ? (
                  <DesignImageSkeleton />
                ) : (
                  <ComparisonViewImage
                    title="Final Design"
                    isOnlyBattery={isOnlyBattery}
                    isOnlyEvCharger={isOnlyEvCharger}
                    batteryImageKey={batteryImageKey}
                    img={smallProposalImage}
                    onOpenLargeImageCarousel={() =>
                      openLargeImageCarousel("new")
                    }
                    showFullSizeImage={showFullSizeImage}
                    testId="offer-experience--DesignStepView--image-final"
                  />
                )}
              </DesignImageColumn>
            </DesignImageRow>
          ) : isLoading ? (
            <DesignImageSkeleton />
          ) : (
            <SingleViewImage
              simple={true}
              isOnlyBattery={isOnlyBattery}
              isOnlyEvCharger={isOnlyEvCharger}
              batteryImageKey={batteryImageKey}
              showFullSizeImage={showFullSizeImage}
              onOpenLargeImageCarousel={() => openLargeImageCarousel("new")}
              smallProposalImage={smallProposalImage}
              testId="offer-experience--DesignStepView--image-single"
            />
          )}
        </SolarDesignSection>
        <CapCollapsibleTechnicalSpecifications
          links={specificationLinks}
          menuLabel="Update design"
          isLoading={isLoading}
          isCustomer={isCustomer}
          isNonSolar={isNonSolar}
        >
          <SystemRows
            viewType={viewType}
            isLoading={isLoading}
            showSelfSupply={true}
            toggleProductionModal={toggleProductionModal}
          />
          <ProductRows viewType={viewType} isLoading={isLoading} />
        </CapCollapsibleTechnicalSpecifications>
      </DesignTableContainer>
    </DesignCard>
  );
};

const DesignCard = styled.section<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  padding: ${({ isMobile }) => (isMobile ? "0 0 16px" : "0 0 24px")};
`;

const DesignTableContainer = styled.span`
  width: 100%;
`;

const SolarDesignSection = styled.div`
  width: 100%;
`;

const DesignImageRow = styled(Row)`
  flex-direction: row;
  gap: ${({ isMobile }) => (isMobile ? "12px" : "60px")};
  padding: 0;
`;

const DesignImageColumn = styled(Column)`
  flex-grow: 1;
  align-items: center;
  width: 50%;

  & > p {
    padding: 8px;
    margin-bottom: 16px;
  }
`;

const DesignImageSkeleton = styled(Skeleton)`
  height: 443px;
  width: 100%;
  margin-bottom: 32px;
`;

export { SimplifiedDesignStepContent };
